import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useNotifications } from '../contexts/NotificationsContext';
import ReferenceBien from '../components/notifications/ReferenceBien';
import { groupeJour, heureNotification, ICONES_TYPE, LIBELLES_TYPE } from '../utils/heureNotification';

const Ligne = ({ ligne, estAdmin, onLu, onOuvrir }) => {
  const nonLue = !ligne.lu_le;
  const reglee = Boolean(ligne.regle_le);
  return (
    <div
      data-testid={`notification-${ligne.id}`}
      className={`px-4 py-3 border-b border-slate-100 flex gap-3 ${nonLue ? 'bg-blue-50/40' : ''}`}
    >
      <div className="pt-0.5 flex flex-col items-center gap-1 shrink-0">
        <i className={`bi ${ICONES_TYPE[ligne.type] || 'bi-bell'} text-slate-400`}></i>
        {nonLue && <span className="w-2 h-2 rounded-full bg-blue-600"></span>}
      </div>

      <div className={`min-w-0 flex-1 ${reglee ? 'text-slate-400' : ''}`}>
        <button
          type="button"
          onClick={() => onOuvrir(ligne)}
          data-testid={`notification-titre-${ligne.id}`}
          className={`text-sm font-semibold text-left min-h-[24px] ${reglee ? 'text-slate-500' : 'text-slate-900'} hover:underline`}
        >
          {ligne.titre} <i className="bi bi-arrow-right-short"></i>
        </button>
        {reglee && (
          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
            Réglé
          </span>
        )}
        <p className="text-[11px] text-slate-400">Ouvre : {ligne.ouvre}</p>
        <p className="text-sm text-slate-700 break-words">{ligne.texte}</p>

        {ligne.property_reference && <ReferenceBien ligne={ligne} onOuvrir={onOuvrir} />}
        {ligne.origine && <p className="text-[11px] text-slate-400">{ligne.origine}</p>}

        {estAdmin && ligne.negociateur_nom && (
          <p className="text-[11px] text-slate-400 mt-0.5">
            Négociateur : {ligne.negociateur_nom}
            {ligne.type === 'support' && (
              ligne.lu_par_auteur_le ? (
                <span className="text-slate-400">
                  {' '}· Lu par {ligne.negociateur_nom.split(' ')[0]} le{' '}
                  {heureNotification(ligne.lu_par_auteur_le)}
                </span>
              ) : (
                <span style={{ color: '#B45309' }}>
                  {' '}· Pas encore lu par {ligne.negociateur_nom.split(' ')[0]}
                </span>
              )
            )}
          </p>
        )}
      </div>

      <div className="text-right shrink-0 flex flex-col items-end gap-1">
        <span className="text-[11px] text-slate-400 whitespace-nowrap">
          {heureNotification(ligne.created_at)}
        </span>
        {nonLue && (
          <button
            type="button"
            onClick={() => onLu(ligne)}
            data-testid={`notification-lu-${ligne.id}`}
            className="text-[11px] text-blue-600 hover:underline min-h-[44px] md:min-h-0"
          >
            Marquer comme lu
          </button>
        )}
      </div>
    </div>
  );
};

/** lot DJ — page Notifications. */
export default function NotificationsPage() {
  const [parametres, setParametres] = useSearchParams();
  const [lignes, setLignes] = useState(null);
  const [erreur, setErreur] = useState(false);
  const [suite, setSuite] = useState(null);
  const [negociateurs, setNegociateurs] = useState([]);
  const { nonLues, total, parType, rafraichir } = useNotifications();
  const navigate = useNavigate();

  const vue = parametres.get('vue') === 'toutes' ? 'toutes' : 'non_lues';
  const type = parametres.get('type') || '';
  const negociateurId = parametres.get('negociateur_id') || '';
  const profil = JSON.parse(localStorage.getItem('user') || '{}');
  const estAdmin = ['admin_agence', 'super_admin'].includes(profil?.role);

  const changer = (modifications) => {
    const suivant = { vue, type, negociateur_id: negociateurId, ...modifications };
    const propres = {};
    Object.entries(suivant).forEach(([cle, valeur]) => { if (valeur) propres[cle] = valeur; });
    setParametres(propres);
  };

  const charger = useCallback(async (avant) => {
    try {
      const { data } = await api.get('/notifications', {
        params: { vue, type: type || undefined, negociateur_id: negociateurId || undefined,
          avant: avant || undefined },
      });
      setLignes((anciennes) => (avant ? [...(anciennes || []), ...data.notifications]
        : data.notifications));
      setSuite(data.page_suivante);
      setErreur(false);
    } catch (_) {
      setErreur(true);
    }
  }, [vue, type, negociateurId]);

  useEffect(() => { setLignes(null); charger(null); }, [charger]);

  useEffect(() => {
    if (!estAdmin) return;
    api.get('/users/negotiators')
      .then(({ data }) => setNegociateurs(data || []))
      .catch(() => setNegociateurs([]));
  }, [estAdmin]);

  const marquerLu = async (ligne) => {
    try { await api.post(`/notifications/${ligne.id}/lu`); } catch (_) { /* sans gravité */ }
    setLignes((anciennes) => (vue === 'non_lues'
      ? (anciennes || []).filter((l) => l.id !== ligne.id)
      : (anciennes || []).map((l) => (l.id === ligne.id ? { ...l, lu_le: new Date().toISOString() } : l))));
    rafraichir();
  };

  const ouvrir = async (ligne, destination) => {
    if (!ligne.lu_le) await marquerLu(ligne);
    navigate(destination || ligne.lien);
  };

  const toutLire = async () => {
    try {
      await api.post('/notifications/tout-lu', null, {
        params: { vue, type: type || undefined, negociateur_id: negociateurId || undefined },
      });
    } catch (_) { /* sans gravité */ }
    charger(null);
    rafraichir();
  };

  let dernierGroupe = null;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-4 overflow-x-hidden text-left"
         data-testid="notifications-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Notifications</h1>
        <button type="button" onClick={toutLire} data-testid="notifications-tout-lu-mobile"
                className="md:hidden text-sm font-semibold text-blue-600 min-h-[44px] px-2">
          Tout lu
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col md:flex-row md:items-center gap-3"
           data-testid="notifications-filtres">
        <div className="flex gap-2 shrink-0">
          {[['non_lues', `Non lues (${nonLues})`], ['toutes', `Toutes (${total})`]].map(([cle, libelle]) => (
            <button
              key={cle}
              type="button"
              onClick={() => changer({ vue: cle })}
              data-testid={`notifications-onglet-${cle}`}
              className={`h-11 px-3 rounded-lg text-sm font-semibold ${
                vue === cle ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}
            >
              {libelle}
            </button>
          ))}
        </div>

        <div className="flex gap-2 overflow-x-auto md:flex-1">
          {Object.entries(parType).filter(([, n]) => n > 0).map(([cle, n]) => (
            <button
              key={cle}
              type="button"
              onClick={() => changer({ type: type === cle ? '' : cle })}
              data-testid={`notifications-type-${cle}`}
              className={`h-11 px-3 rounded-full text-xs font-semibold whitespace-nowrap border ${
                type === cle ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-slate-200 text-slate-600'}`}
            >
              {LIBELLES_TYPE[cle] || cle} ({n})
            </button>
          ))}
        </div>

        {estAdmin && (
          <select
            value={negociateurId}
            onChange={(e) => changer({ negociateur_id: e.target.value })}
            data-testid="notifications-negociateur"
            className="h-11 px-2 rounded-lg border border-slate-300 text-sm bg-white"
          >
            <option value="">Tous les négociateurs</option>
            {negociateurs.map((u) => (
              <option key={u.id} value={u.id}>{`${u.prenom || ''} ${u.nom || ''}`.trim() || u.email}</option>
            ))}
          </select>
        )}

        <button type="button" onClick={toutLire} data-testid="notifications-tout-lu"
                className="hidden md:block h-11 px-3 text-sm font-semibold text-blue-600 shrink-0">
          Tout marquer comme lu
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {erreur ? (
          <div className="p-8 text-center" data-testid="notifications-erreur">
            <p className="text-sm text-slate-600">Les notifications n'ont pas pu être chargées.</p>
            <button type="button" onClick={() => charger(null)}
                    className="mt-3 h-11 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold">
              Réessayer
            </button>
          </div>
        ) : lignes === null ? (
          <div className="p-4 space-y-3" data-testid="notifications-chargement">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 rounded-lg bg-slate-100 animate-pulse"></div>
            ))}
          </div>
        ) : lignes.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500" data-testid="notifications-vide">
            {vue === 'non_lues' ? 'Aucune notification non lue' : 'Aucune notification'}
          </p>
        ) : (
          <>
            {lignes.map((ligne) => {
              const groupe = groupeJour(ligne.created_at);
              const nouveauGroupe = groupe !== dernierGroupe;
              dernierGroupe = groupe;
              return (
                <React.Fragment key={ligne.id}>
                  {nouveauGroupe && (
                    <p className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-50">
                      {groupe}
                    </p>
                  )}
                  <Ligne ligne={ligne} estAdmin={estAdmin} onLu={marquerLu} onOuvrir={ouvrir} />
                </React.Fragment>
              );
            })}
            {suite && (
              <button type="button" onClick={() => charger(suite)}
                      data-testid="notifications-charger-plus"
                      className="w-full h-12 text-sm font-semibold text-blue-600 hover:bg-slate-50">
                Charger plus
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
